package or.garden.manager.features.beds.components

import androidx.compose.runtime.*
import or.garden.manager.domain.model.CropData
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Composable
fun AiSelectionModal(
    availableCrops: List<CropData>,
    onClose: () -> Unit,
    onGenerate: (List<CropData>, Boolean, Int) -> Unit
) {
    var selectedCrops by remember { mutableStateOf(setOf<Int>()) }
    var includePathways by remember { mutableStateOf(true) }
    var pathwayWidth by remember { mutableStateOf(40) }

    Div({
        classes("fixed", "inset-0", "bg-black/50", "flex", "items-center", "justify-center", "z-[120]", "p-4")
        onClick { onClose() }
    }) {
        Div({
            classes("bg-white", "rounded-3xl", "max-w-4xl", "w-full", "max-h-[90vh]", "flex", "flex-col", "shadow-2xl")
            onClick { it.stopPropagation() }
        }) {
            Div({
                classes("p-8", "border-b", "border-gray-100", "flex", "justify-between", "items-center")
            }) {
                Div {
                    H2({ classes("text-2xl", "font-bold", "text-gray-900") }) { Text("AI Planer: Izaberi kulture") }
                    P({ classes("text-sm", "text-gray-500", "mt-1") }) { Text("Vještačka inteligencija će rasporediti izabrane biljke poštujući plodored i pratioce.") }
                }
                Button({
                    classes("text-gray-400", "hover:text-gray-600"); onClick { onClose() }
                }) { I({ classes("fas", "fa-times", "text-xl") }) }
            }

            Div({ classes("p-8", "overflow-y-auto", "flex-1") }) {
                Div({ classes("flex", "flex-col", "sm:flex-row", "gap-4", "mb-8") }) {
                    Div({ classes("flex-1", "p-6", "bg-emerald-50", "rounded-2xl", "flex", "items-center", "gap-3") }) {
                        Input(InputType.Checkbox) {
                            id("include-pathways-ai")
                            checked(includePathways)
                            onInput { includePathways = it.value }
                        }
                        Label(forId = "include-pathways-ai", attrs = {
                            classes("text-emerald-900", "font-bold", "cursor-pointer")
                        }) { Text("Uključi staze") }
                    }

                    if (includePathways) {
                        Div({ classes("flex-1", "p-6", "bg-amber-50", "rounded-2xl", "flex", "flex-col", "gap-4") }) {
                            Label(forId = "pathway-width-ai", attrs = {
                                classes("text-amber-900", "font-bold", "whitespace-nowrap")
                            }) { Text("Širina staze (cm):") }

                            Div({ classes("grid", "grid-cols-5", "gap-2", "w-full") }) {
                                listOf(20, 30, 40, 50).forEach { width ->
                                    val isSelected = width == pathwayWidth
                                    Div({
                                        classes("py-2", "px-3", "rounded-xl", "text-center", "font-bold", "cursor-pointer", "transition-all")
                                        if (isSelected) classes("bg-emerald-600", "text-white", "shadow-md")
                                        else classes("bg-white", "text-amber-900", "border", "border-amber-200", "hover:bg-amber-100")
                                        onClick { pathwayWidth = width }
                                    }) {
                                        Text(width.toString())
                                    }
                                }
                            }
                        }
                    }
                }

                Div({
                    classes("grid", "grid-cols-2", "sm:grid-cols-3", "md:grid-cols-4", "gap-4")
                }) {
                    availableCrops.filter { it.id != -1 && !it.isCompanion }.forEach { crop ->
                        val isSelected = selectedCrops.contains(crop.id)
                        Div({
                            classes("p-4", "bg-white", "border-2", "rounded-2xl", "transition-all", "cursor-pointer", "flex", "flex-col", "items-center", "gap-3")
                            if (isSelected) classes("border-emerald-500", "bg-emerald-50") else classes("border-gray-100", "hover:border-emerald-200")
                            onClick {
                                selectedCrops = if (isSelected) selectedCrops - crop.id else selectedCrops + crop.id
                            }
                        }) {
                            Div({
                                classes("w-12", "h-12", "bg-white", "rounded-xl", "flex", "items-center", "justify-center", "shadow-sm")
                            }) {
                                val icon = getCropImageSource(crop.icon)
                                if (icon != null) Img(src = icon, attrs = { classes("w-10", "h-10", "object-contain") })
                                else I({ classes("fas", "fa-leaf", "text-emerald-600", "text-xl") })
                            }
                            Span({ classes("text-sm", "font-bold", "text-gray-800", "text-center") }) { Text(crop.name) }
                            if (isSelected) {
                                Div({ classes("absolute", "top-2", "right-2", "w-5", "h-5", "bg-emerald-500", "rounded-full", "flex", "items-center", "justify-center") }) {
                                    I({ classes("fas", "fa-check", "text-white", "text-[10px]") })
                                }
                            }
                        }
                    }
                }
            }

            Div({ classes("p-8", "bg-gray-50", "rounded-b-3xl", "flex", "justify-end", "gap-4") }) {
                Button({
                    classes("px-8", "py-3", "bg-white", "text-gray-600", "border", "border-gray-200", "rounded-xl", "font-bold", "hover:bg-gray-100")
                    onClick { onClose() }
                }) { Text("Otkaži") }

                Button({
                    val btnClasses = mutableListOf("px-8", "py-3", "bg-emerald-600", "text-white", "rounded-xl", "font-bold", "shadow-lg", "hover:bg-emerald-700", "transition-all")
                    if (selectedCrops.isEmpty()) btnClasses.add("opacity-50")
                    classes(btnClasses)
                    if (selectedCrops.isNotEmpty()) {
                        onClick { 
                            onGenerate(availableCrops.filter { selectedCrops.contains(it.id) }, includePathways, pathwayWidth)
                        }
                    }
                }) {
                    I({ classes("fas", "fa-magic", "mr-2") })
                    Text("Generiši plan")
                }
            }
        }
    }
}
